import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import GroupsIcon from "@mui/icons-material/Groups";
import CloseIcon from "@mui/icons-material/Close";
import Groups2Icon from "@mui/icons-material/Groups2";
import EmailIcon from "@mui/icons-material/Email";
import SecurityIcon from "@mui/icons-material/Security";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useTheme } from "@emotion/react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import { useNavigate } from "react-router-dom";

const getMenuItems = (t) => [
  {
    label: t('dashboard'),
    icon: <DashboardIcon />,
    path: "/tenant/dashboard",
    tone: "#3e4a6b",
  },
  {
    label: t('profile'),
    icon: <AccountCircleIcon />,
    path: "/tenant/profile",
    tone: "#445372",
  },
  {
    label: "Plan & Quotas",
    icon: <WorkspacePremiumIcon />,
    path: "/tenant/subscription",
    tone: "#2f6f7f",
  },
  {
    label: t('doctors'),
    icon: <Groups2Icon />,
    path: "/tenant/doctors",
    tone: "#3b5b52",
  },
  {
    label: t('inviteAdminDoctor'),
    icon: <EmailIcon />,
    path: "/tenant/invite-admin-doctor",
    tone: "#5f493f",
  },
  {
    label: t('patients'),
    icon: <GroupsIcon />,
    path: "/tenant/patients",
    tone: "#4f3f6a",
  },
  {
    label: t('permissionGroups'),
    icon: <SecurityIcon />,
    path: "/tenant/permission-groups",
    tone: "#5a3f5f",
  },
];

export default function SidebarTenant({
  menuOpen,
  setMenuOpen,
  variant = "temporary",
  drawerWidth = 300,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('tenantDashboard');
  const navigate = useNavigate();
  const menuItems = useMemo(() => getMenuItems(t), [t]);

  const drawerBackground = useMemo(
    () =>
      `linear-gradient(160deg, ${colors.primary[500]}f5 0%, ${colors.primary[600]}f0 60%, ${colors.primary[700]}ee 100%)`,
    [colors]
  );

  return (
    <Drawer
      anchor="left"
      open={menuOpen}
      onClose={() => setMenuOpen(false)}
      variant={variant}
      ModalProps={variant === "temporary" ? { keepMounted: true } : undefined}
      sx={{
        display:
          variant === "persistent"
            ? { xs: "none", md: "block" }
            : undefined,
        "& .MuiDrawer-paper": {
          width:
            variant === "persistent"
              ? drawerWidth
              : { xs: 260, sm: 300 },
          background: "transparent",
          boxShadow: "none",
        },
      }}
      PaperProps={{
        sx: {
          background: "transparent",
          boxShadow: "none",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          background: drawerBackground,
          backdropFilter: "blur(14px)",
          borderRight: `1px solid ${colors.primary[700]}66`,
          boxShadow: `8px 0 35px rgba(10, 12, 24, 0.28)`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              variant="rounded"
              sx={{
                width: 48,
                height: 48,
                backgroundColor: colors.blueAccent[500],
                color: colors.grey[100],
                boxShadow: `0 8px 24px rgba(59, 130, 246, 0.35)`,
              }}
            >
              <DashboardIcon sx={{ fontSize: 26 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, letterSpacing: 0.3, color: colors.grey[100] }}
              >
                {t('menu')}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[400], opacity: 0.9 }}>
                {t('quickAccessToTenantTools')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setMenuOpen(false)}
            sx={{
              borderRadius: 2,
              backgroundColor: `${colors.primary[500]}aa`,
              border: `1px solid ${colors.primary[600]}55`,
              "&:hover": {
                backgroundColor: `${colors.primary[500]}dd`,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 22, color: colors.grey[100] }} />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: `${colors.primary[700]}66`, mx: 3 }} />

        <List
          sx={{
            py: 2,
            px: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {menuItems.map((item) => (
            <ListItemButton
              key={item.label}
              onClick={() => {
                navigate(item.path);
                setMenuOpen(false);
              }}
              sx={{
                mx: 2,
                borderRadius: 3,
                py: 1.6,
                px: 2,
                backgroundColor: `${colors.primary[600]}88`,
                border: `1px solid ${colors.primary[700]}66`,
                boxShadow: "0 4px 14px rgba(3, 10, 30, 0.45)",
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  backgroundColor: `${item.tone}dd`,
                  boxShadow: "0 14px 32px rgba(3, 10, 30, 0.55)",
                },
              }}
            >
              <Avatar
                variant="rounded"
                sx={{
                  width: 42,
                  height: 42,
                  backgroundColor: item.tone,
                  color: "#ffffff",
                  boxShadow: `0 10px 24px rgba(0, 0, 0, 0.35)`,
                  mr: 2,
                }}
              >
                {item.icon}
              </Avatar>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  sx: {
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    color: colors.grey[100],
                  },
                }}
                secondary={t('openSection')}
                secondaryTypographyProps={{
                  sx: { fontSize: "0.75rem", color: colors.grey[400] },
                }}
              />
            </ListItemButton>
          ))}
        </List>

        <Box sx={{ px: 3, py: 2.5, mt: "auto" }}>
          <Box
            sx={{
              borderRadius: 3,
              border: `1px dashed ${colors.primary[700]}aa`,
              backgroundColor: `${colors.primary[600]}aa`,
              px: 2.5,
              py: 2,
              textAlign: "left",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: colors.grey[100], mb: 0.5 }}
            >
              {t('needAssistance')}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[400] }}>
              {t('visitDashboardForAnalytics')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
